import { useState, type FormEvent, type ReactNode } from 'react';
import { Check } from 'lucide-react';
import { Button } from '@/components/Button';
import { Banner } from '@/components/Banner';
import { Casilla } from '@/components/Casilla';
import { DateField, NumberField, SelectField, TextField } from '@/components/Field';
import { guardarComida } from '@/db/comidas';
import { mensajeDeError } from '@/db/tx';
import type { CalidadDato, EstadoCoccion, MealEntry, TiempoComida } from '@/db/types';
import { ahoraIso, esFechaLocal } from '@/domain/dates';
import { esHoraLocal } from '@/domain/time';
import { leerDecimal } from '@/domain/numbers';
import {
  NOTAS_CALIDAD,
  calcular,
  opcionesDePorcion,
  validarRegistro,
  type Calculable,
} from '@/domain/nutricion';
import { CampoHora, OPCIONES_TIEMPO, Opciones, VistaValores } from './comunes';

/** Lo que se registra: de la base, de Mis alimentos o de un registro anterior. */
export interface Fuente {
  nombre: string;
  origen: MealEntry['origen'];
  calculable: Calculable;
  rompeAyuno: boolean;
  estado: EstadoCoccion | null;
  esBase: boolean;
  /** De dónde salen los valores, si no es lo de siempre. */
  nota?: string;
  /** La calidad del dato del alimento propio, que se copia al registro (0.3.5). */
  calidad?: CalidadDato | null;
}

export interface InicialRegistro {
  /** Creado al abrir el formulario, o el del registro que se edita. */
  id: string;
  fecha: string;
  hora: string;
  tiempo: TiempoComida;
  cantidad: string;
  porcion: string;
  nota: string;
  creadoEn: string;
  /** Al editar: la comida habitual con que se registró; se conserva si no cambia de día (0.3.5). */
  habitual?: string | null;
}

interface Props {
  fuente: Fuente;
  inicial: InicialRegistro;
  textoGuardar: string;
  onGuardado: (registro: MealEntry) => void;
  /** Acciones extra bajo el botón principal, como Duplicar o Borrar. */
  extra?: ReactNode;
}

/**
 * Cantidad y porción con la vista previa de valores antes de guardar
 * (Req. 18.1, 18.3, 18.7). El id viene de quien abre el formulario y se guarda
 * con put: tocar dos veces "Guardar" no duplica (Req. 1.9).
 */
export function FormularioRegistro({ fuente, inicial, textoGuardar, onGuardado, extra }: Props) {
  const opciones = opcionesDePorcion(fuente.calculable);
  const porcionInicial = opciones.some((o) => o.clave === inicial.porcion)
    ? inicial.porcion
    : (opciones[0]?.clave ?? 'g');
  const [cantidad, setCantidad] = useState(inicial.cantidad);
  const [porcion, setPorcion] = useState(porcionInicial);
  const [tiempo, setTiempo] = useState<TiempoComida>(inicial.tiempo);
  const [fecha, setFecha] = useState(inicial.fecha);
  const [hora, setHora] = useState(inicial.hora);
  const [nota, setNota] = useState(inicial.nota);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const numero = leerDecimal(cantidad);
  const calculo = numero === null ? null : calcular(fuente.calculable, numero, porcion);
  /** El cálculo correcto, o null si todavía no se puede calcular. */
  const resultado = calculo !== null && calculo.ok ? calculo : null;
  const valido = resultado !== null;
  const motivo =
    numero === null
      ? 'Escribe la cantidad.'
      : calculo !== null && !calculo.ok
        ? calculo.error
        : resultado !== null
          ? validarRegistro({ nombre: fuente.nombre, cantidad: numero, gramos: resultado.gramos, valores: resultado.valores })
          : null;

  async function guardar(evento: FormEvent) {
    evento.preventDefault();
    if (guardando || numero === null || resultado === null) return;
    const calculo = resultado;
    if (!esFechaLocal(fecha)) return setError('Elige la fecha.');
    if (!esHoraLocal(hora)) return setError('Elige la hora.');
    setError(null);
    setGuardando(true);
    try {
      const registro = await guardarComida({
        id: inicial.id,
        fecha,
        hora,
        momento: '',
        tiempo,
        origen: fuente.origen,
        nombre: fuente.nombre,
        cantidad: numero,
        porcion,
        gramos: calculo.gramos,
        valores: calculo.valores,
        rompeAyuno: fuente.rompeAyuno,
        estado: fuente.estado,
        ...(fuente.calidad === undefined || fuente.calidad === null ? {} : { calidad: fuente.calidad }),
        // Movido a otro día ya no es el registro de su comida habitual: ese día se puede confirmar aparte
        ...(inicial.habitual === undefined || inicial.habitual === null || fecha !== inicial.fecha
          ? {}
          : { habitual: inicial.habitual }),
        nota: nota.trim() === '' ? null : nota,
        creadoEn: inicial.creadoEn,
        actualizadoEn: ahoraIso(),
      });
      onGuardado(registro);
    } catch (e) {
      setError(mensajeDeError(e));
      setGuardando(false);
    }
  }

  return (
    <form onSubmit={(e) => void guardar(e)} className="grid gap-[17px]" noValidate>
      <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-3">
        <NumberField etiqueta="Cantidad" valor={cantidad} onCambio={setCantidad} />
        <SelectField
          etiqueta="Porción"
          valor={porcion}
          onCambio={setPorcion}
          opciones={opciones.map((o) => ({ valor: o.clave, etiqueta: o.etiqueta }))}
        />
      </div>

      {resultado !== null ? (
        <VistaValores
          valores={resultado.valores}
          gramos={resultado.gramos}
          nota={
            fuente.nota ??
            (fuente.esBase
              ? 'Valores aproximados de USDA FoodData Central.'
              : fuente.origen.tipo === 'propio'
                ? fuente.calidad === undefined || fuente.calidad === null
                  ? 'Valores de tu alimento, como los capturaste de su etiqueta.'
                  : NOTAS_CALIDAD[fuente.calidad]
                : fuente.origen.tipo === 'platillo'
                  ? 'Calculado con los ingredientes del platillo como están hoy.'
                  : 'Valores del registro original.')
          }
        />
      ) : (
        <p className="rounded-panel bg-superficie p-5 text-[13px] text-texto-suave">
          {motivo ?? 'Escribe la cantidad para ver sus valores.'}
        </p>
      )}

      <Opciones etiqueta="Tiempo de comida" valor={tiempo} opciones={OPCIONES_TIEMPO} onCambio={setTiempo} columnas={4} />

      {/* Uno debajo del otro: los campos nativos de fecha y hora no caben juntos en 320 px */}
      <DateField etiqueta="Fecha" valor={fecha} onCambio={setFecha} />
      <CampoHora etiqueta="Hora" valor={hora} onCambio={setHora} />

      <TextField etiqueta="Nota (opcional)" valor={nota} onCambio={setNota} maxLength={120} />

      {valido && motivo !== null && (
        <Banner tono="alerta" titulo="No se puede guardar">
          {motivo}
        </Banner>
      )}
      {error !== null && (
        <Banner tono="alerta" titulo="No se guardó">
          {error}
        </Banner>
      )}

      <div className="space-y-2">
        <Button type="submit" icono={Check} disabled={guardando || !valido || motivo !== null}>
          {guardando ? 'Guardando…' : textoGuardar}
        </Button>
        {extra}
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Registro rápido (Req. 18.2)
// ---------------------------------------------------------------------------

export interface InicialRapido extends InicialRegistro {
  nombre: string;
  kcal: string;
  proteina: string;
  carbohidratos: string;
  grasa: string;
  rompeAyuno: boolean;
}

/** Nombre y kcal; los macros son opcionales y vacíos quedan "sin dato". */
export function FormularioRapido({
  inicial,
  textoGuardar,
  onGuardado,
  extra,
}: {
  inicial: InicialRapido;
  textoGuardar: string;
  onGuardado: (registro: MealEntry) => void;
  extra?: ReactNode;
}) {
  const [nombre, setNombre] = useState(inicial.nombre);
  const [kcal, setKcal] = useState(inicial.kcal);
  const [proteina, setProteina] = useState(inicial.proteina);
  const [carbohidratos, setCarbohidratos] = useState(inicial.carbohidratos);
  const [grasa, setGrasa] = useState(inicial.grasa);
  const [rompeAyuno, setRompeAyuno] = useState(inicial.rompeAyuno);
  const [tiempo, setTiempo] = useState<TiempoComida>(inicial.tiempo);
  const [fecha, setFecha] = useState(inicial.fecha);
  const [hora, setHora] = useState(inicial.hora);
  const [nota, setNota] = useState(inicial.nota);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const opcional = (texto: string) => (texto.trim() === '' ? null : leerDecimal(texto));
  const kcalNumero = leerDecimal(kcal);

  async function guardar(evento: FormEvent) {
    evento.preventDefault();
    if (guardando) return;
    if (nombre.trim() === '') return setError('Escribe qué comiste.');
    if (kcalNumero === null) return setError('Escribe las kcal.');
    const macros = [proteina, carbohidratos, grasa].map(opcional);
    if ([proteina, carbohidratos, grasa].some((t, i) => t.trim() !== '' && macros[i] === null)) {
      return setError('Revisa los macros: deben ser números.');
    }
    if (!esFechaLocal(fecha)) return setError('Elige la fecha.');
    if (!esHoraLocal(hora)) return setError('Elige la hora.');
    setError(null);
    setGuardando(true);
    try {
      const registro = await guardarComida({
        id: inicial.id,
        fecha,
        hora,
        momento: '',
        tiempo,
        origen: { tipo: 'rapido' },
        nombre,
        cantidad: 1,
        porcion: null,
        gramos: null,
        valores: {
          kcal: kcalNumero,
          proteinaG: macros[0] ?? null,
          carbohidratosG: macros[1] ?? null,
          grasaG: macros[2] ?? null,
          fibraG: null,
        },
        rompeAyuno,
        estado: null,
        nota: nota.trim() === '' ? null : nota,
        creadoEn: inicial.creadoEn,
        actualizadoEn: ahoraIso(),
      });
      onGuardado(registro);
    } catch (e) {
      setError(mensajeDeError(e));
      setGuardando(false);
    }
  }

  return (
    <form onSubmit={(e) => void guardar(e)} className="grid gap-[17px]" noValidate>
      <TextField etiqueta="Qué comiste" valor={nombre} onCambio={setNombre} maxLength={80} placeholder="Tacos de canasta" />
      <NumberField etiqueta="kcal" valor={kcal} onCambio={setKcal} />
      <fieldset className="grid gap-3">
        <legend className="mb-1 text-[13px] text-texto-suave">Macros (opcionales, en gramos)</legend>
        <div className="grid grid-cols-3 gap-3">
          <NumberField etiqueta="Proteína" valor={proteina} onCambio={setProteina} />
          <NumberField etiqueta="Carbohidratos" valor={carbohidratos} onCambio={setCarbohidratos} />
          <NumberField etiqueta="Grasa" valor={grasa} onCambio={setGrasa} />
        </div>
        <p className="text-[12px] text-texto-suave">
          Si no los sabes, déjalos vacíos: la app los marca como datos incompletos, no como cero.
        </p>
      </fieldset>

      <Opciones etiqueta="Tiempo de comida" valor={tiempo} opciones={OPCIONES_TIEMPO} onCambio={setTiempo} columnas={4} />
      {/* Uno debajo del otro: los campos nativos de fecha y hora no caben juntos en 320 px */}
      <DateField etiqueta="Fecha" valor={fecha} onCambio={setFecha} />
      <CampoHora etiqueta="Hora" valor={hora} onCambio={setHora} />
      <Casilla marcada={!rompeAyuno} onCambio={(m) => setRompeAyuno(!m)}>
        No rompe el ayuno (agua, café o té sin azúcar)
      </Casilla>
      <TextField etiqueta="Nota (opcional)" valor={nota} onCambio={setNota} maxLength={120} />

      {error !== null && (
        <Banner tono="alerta" titulo="No se guardó">
          {error}
        </Banner>
      )}
      <div className="space-y-2">
        <Button type="submit" icono={Check} disabled={guardando}>
          {guardando ? 'Guardando…' : textoGuardar}
        </Button>
        {extra}
      </div>
    </form>
  );
}
